/**
 * BuildingsManagement - Manage accommodation buildings
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Building, Plus, Edit2, Trash2, MapPin, Phone, User,
  Wifi, Car, Coffee, AlertTriangle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AMENITIES_OPTIONS = [
  { id: 'wifi', label: 'WiFi', icon: Wifi },
  { id: 'parking', label: 'Parking', icon: Car },
  { id: 'kitchen', label: 'Kitchen', icon: Coffee },
  { id: 'laundry', label: 'Laundry' },
  { id: 'security', label: '24h Security' },
  { id: 'cctv', label: 'CCTV' },
  { id: 'ac', label: 'Air Conditioning' },
  { id: 'generator', label: 'Backup Generator' },
];

const BuildingsManagement = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch buildings
  const { data: buildings = [], isLoading } = useQuery({
    queryKey: ['accommodation-buildings'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/buildings`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Create building
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/buildings`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create building');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-buildings']);
      setShowAddDialog(false);
      toast.success('Building created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update building
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/buildings/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update building');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-buildings']);
      setEditingBuilding(null);
      toast.success('Building updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete building
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/buildings/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete building');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-buildings']);
      setDeleteConfirm(null);
      toast.success('Building deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  const BuildingForm = ({ building, onSubmit, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
      name: building?.name || '',
      description: building?.description || '',
      address: building?.address || '',
      total_floors: building?.total_floors || 1,
      amenities: building?.amenities || [],
      contact_person: building?.contact_person || '',
      contact_phone: building?.contact_phone || ''
    });

    const toggleAmenity = (amenityId) => {
      setFormData(prev => ({
        ...prev,
        amenities: prev.amenities.includes(amenityId)
          ? prev.amenities.filter(a => a !== amenityId)
          : [...prev.amenities, amenityId]
      }));
    };

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
        className="space-y-4"
      >
        <div>
          <label className="text-sm font-medium">Building Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder="e.g., Main Guest House"
            data-testid="input-building-name"
          />
        </div>

        <div>
          <label className="text-sm font-medium">Description</label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of the building"
            rows={2}
          />
        </div>

        <div>
          <label className="text-sm font-medium">Address</label>
          <Input
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            placeholder="Street address"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Total Floors</label>
            <Input
              type="number"
              min="1"
              value={formData.total_floors}
              onChange={(e) => setFormData({ ...formData, total_floors: parseInt(e.target.value) || 1 })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Contact Person</label>
            <Input
              value={formData.contact_person}
              onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              placeholder="Manager name"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Contact Phone</label>
          <Input
            value={formData.contact_phone}
            onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
            placeholder="+1234567890"
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Amenities</label>
          <div className="grid grid-cols-2 gap-2">
            {AMENITIES_OPTIONS.map((amenity) => (
              <div
                key={amenity.id}
                className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                  formData.amenities.includes(amenity.id)
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200'
                }`}
                onClick={() => toggleAmenity(amenity.id)}
              >
                <Checkbox checked={formData.amenities.includes(amenity.id)} />
                <span className="text-sm">{amenity.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading} data-testid="submit-building-btn">
            {isLoading ? 'Saving...' : building ? 'Update Building' : 'Create Building'}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6" data-testid="buildings-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Buildings</h2>
          <p className="text-sm text-gray-500">Manage accommodation buildings and properties</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-building-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Building
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading buildings...</div>
      ) : buildings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No buildings yet</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add your first building to start managing rooms
            </p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Building
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {buildings.map((building) => (
            <Card key={building.id} data-testid={`building-card-${building.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building className="h-5 w-5 text-indigo-600" />
                    {building.name}
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditingBuilding(building)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(building)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {building.description && (
                  <p className="text-sm text-gray-500">{building.description}</p>
                )}
                
                {building.address && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <MapPin className="h-4 w-4" />
                    {building.address}
                  </div>
                )}

                <div className="flex items-center gap-4 text-sm">
                  <span>{building.total_floors} floor(s)</span>
                  {building.contact_person && (
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {building.contact_person}
                    </span>
                  )}
                </div>

                {/* Room Stats */}
                {building.room_stats && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1 pt-2 border-t">
                    <div className="text-center">
                      <p className="text-lg font-bold">{building.room_stats.total}</p>
                      <p className="text-xs text-gray-500">Total</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-green-600">{building.room_stats.ready}</p>
                      <p className="text-xs text-gray-500">Ready</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-blue-600">{building.room_stats.occupied}</p>
                      <p className="text-xs text-gray-500">Occupied</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-yellow-600">{building.room_stats.dirty}</p>
                      <p className="text-xs text-gray-500">Dirty</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-red-600">{building.room_stats.maintenance}</p>
                      <p className="text-xs text-gray-500">Maint.</p>
                    </div>
                  </div>
                )}

                {/* Amenities */}
                {building.amenities?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-2">
                    {building.amenities.map((amenity) => (
                      <Badge key={amenity} variant="secondary" className="text-xs">
                        {amenity}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Building Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Building</DialogTitle>
          </DialogHeader>
          <BuildingForm
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddDialog(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Building Dialog */}
      <Dialog open={!!editingBuilding} onOpenChange={(open) => !open && setEditingBuilding(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Building</DialogTitle>
          </DialogHeader>
          {editingBuilding && (
            <BuildingForm
              building={editingBuilding}
              onSubmit={(data) => updateMutation.mutate({ id: editingBuilding.id, data })}
              onCancel={() => setEditingBuilding(null)}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Building
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This will also affect all rooms in this building.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate(deleteConfirm.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Building'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BuildingsManagement;
